import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerPostingTemplateCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type { PostingTemplateDto } from "@/features/ledger/api/dto";

/**
 * Archive un modèle d'imputation (`POST /PostingTemplates/{id}/archive`) —
 * réservé à flashpay-superadmin / flashpay-admin. Les écritures déjà
 * passées ne sont pas affectées.
 */
export const useArchivePostingTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "posting-templates", "archive"],
    mutationFn: (postingTemplateId: string) =>
      mutateData({
        url: ledgerEndpoints.postingTemplates.archive(postingTemplateId),
        method: "POST",
      }) as Promise<PostingTemplateDto>,
    onSuccess: (_data, postingTemplateId) => {
      return invalidateAfterMutation(
        queryClient,
        ledgerPostingTemplateCacheKeys(postingTemplateId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useArchivePostingTemplate failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
